import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { bindActionCreators } from 'redux'
import {
  checkValidations,
  positiveNumberRule,
  requiredRule
} from 'src/util/validation/commonValidation.helpers'

import { toast } from 'src/components/shared/snackbar'
import { useFilterExchangeWhitelist } from 'wrappers/useFilterExchangeWhitelist'
import ExchangePoints from '../components/exchangePoints/ExchangePoints'
import ReceiveExternalRewardsInit from '../components/popups/exchange/ReceiveExternalRewardsInit'
import CameraBlocked from '../components/popups/qrCode/CameraBlocked'
import ConnectAccountByQrCode from '../components/popups/qrCode/ConnectAccountByQrCode'
import {
  DEFAULT_CONTAINER_STATE,
  EXCHANGE_PROVIDER_PROCESS_STEP,
  QR_CODE_PROCESS_STEP,
  REDEEM_POINTS_PROCESS_STEP
} from '../constants/containerStates'
import { ERROR_TEXT } from '../constants/messages'
import { SETTINGS_URL } from '../constants/navigation'
import { MIN_USD_FOR_EXCHANGE } from '../constants/pointsExchange'
import {
  changeExchangeProvidersInputAndErrorState,
  changeExchangeProvidersState,
  connectExchangeProvider,
  getCryptoExchangeRates,
  receiveExternalRewardFromBrandPoints,
  resetExchangeProvider,
  stopGetCryptoExchangeRates
} from '../store/actions/exchange'
import { changeKYCState } from '../store/actions/kyc'
import { changeRewardsState, resetRewardsState } from '../store/actions/rewards'
import { KYC_PROFILE_DISPATCH } from '../store/reducers/account/kyc'
import { EXCHANGE_PROVIDER_PROFILE_DISPATCH } from '../store/reducers/exchange/exchangeProviders'
import { REWARDS_PROFILE_DISPATCH } from '../store/reducers/rewards/rewards'
import { openExternalUrl } from '../util/app/app.helpers'
import { isKycIncomplete } from '../util/kyc.helpers'
import { containsNoValues } from '../util/object.helpers'
import {
  buildSignUpUrl,
  calculateMinMaxPoints,
  checkExchangeGating,
  exchangeProviderAccountLabel,
  extractMembershipNumber,
  filterExchangeProviderForExchange,
  isProviderConnected
} from '../util/pointsExchange.helpers'
import { SETTING_FILTER_TYPE } from '../util/settings.helpers'
import {
  EMERALD_LEVEL_ID,
  ONYX_LEVEL_ID
} from '../util/staking/staking.helpers'
import { extractCodeOrInputText } from '../util/string.helpers'
import {
  minPointsRule,
  validationRulesForAccountNumberSanity
} from '../util/validation/validation.helpers'

const ExchangePointsContainer = ({
  rewardsProfile,
  exchangeProviderProfile,
  mainProfile,
  tokenProfile,
  changeExchangeProvidersState,
  changeExchangeProvidersInputAndErrorState,
  connectExchangeProvider,
  resetExchangeProvider,
  receiveExternalRewardFromBrandPoints,
  resetRewardsState,
  changeRewardsState,
  getCryptoExchangeRates,
  stopGetCryptoExchangeRates,
  cryptoExchanges,
  kycProfile,
  changeKYCState
}) => {
  const { t } = useTranslation()

  const {
    currentUserPoints,
    exchangeMemberships,
    isDisabledPointsBurn,
    userTier
  } = mainProfile

  const navigate = useNavigate()

  useEffect(() => {
    handleNoPopup()
    return () => {
      resetRewardsState()
      resetExchangeProvider()
    }
  }, [])

  useEffect(() => {
    const redeemState = rewardsProfile.redeemState
    if (redeemState) {
      // stop polling exchange-rates when points to crypto is success
      if (
        redeemState === REDEEM_POINTS_PROCESS_STEP.EXTERNAL_REWARDS_REDEEMED
      ) {
        stopGetCryptoExchangeRates()
      }
      handleNoPopup()
    }
  }, [rewardsProfile.redeemState])

  useEffect(() => {
    if (
      exchangeProviderProfile?.accountSaveState ===
      EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER
    ) {
      handleAccountSaveState(exchangeProviderProfile?.accountSaveState)
    }
  }, [exchangeProviderProfile?.accountSaveState])

  const handleAccountSaveState = (state) => {
    if (!provider?.brandName) return
    const messages = {
      [EXCHANGE_PROVIDER_PROCESS_STEP.CONNECTED_ACCOUNT]: 'common.connected'
    }
    if (messages[state]) {
      toast.success(
        t('exchange.account-saved-success', {
          providerName: provider.brandName,
          label: t(messages[state])
        })
      )
    } else if (state === EXCHANGE_PROVIDER_PROCESS_STEP.WRONG_ACCOUNT_NUMBER) {
      wrongAccountNumberToast()
    }
  }

  const wrongAccountNumberToast = () => {
    if (provider?.brandName) {
      toast.error(
        t('exchange.incorrect-account-number', {
          providerName: provider.brandName,
          fieldLabel: exchangeProviderAccountLabel(t, provider, true)
        })
      )
    }
  }

  const filteredExchangeWhitelist = useFilterExchangeWhitelist(
    mainProfile.exchangeWhitelist
  )

  const exchangeWhitelist = useMemo(() => {
    return filterExchangeProviderForExchange(filteredExchangeWhitelist)
  }, [filteredExchangeWhitelist])

  const [popupState, setPopupState] = useState()
  const [provider, setProvider] = useState()
  const [signUpUrl, setSignUpUrl] = useState()

  useEffect(() => {
    if (provider) {
      const membership = exchangeMemberships[provider.brandId]
      if (membership) {
        changeExchangeProvidersState(
          EXCHANGE_PROVIDER_PROFILE_DISPATCH.CONNECT_ACCOUNT_NUMBER,
          membership.membershipNumber
        )
      } else {
        changeExchangeProvidersState(
          EXCHANGE_PROVIDER_PROFILE_DISPATCH.CONNECT_ACCOUNT_NUMBER,
          ''
        )
      }
      if (provider.isCrypto) {
        getCryptoExchangeRates()
      } else {
        stopGetCryptoExchangeRates()
      }
      const fetchSignupUrl = async () => {
        const signUpUrl = await buildSignUpUrl(provider)
        setSignUpUrl(signUpUrl)
      }
      fetchSignupUrl().catch(console.error)
    } else {
      stopGetCryptoExchangeRates()
    }
    return () => {
      stopGetCryptoExchangeRates()
    }
  }, [provider])

  const handleConnectProvider = () => {
    if (isConnected) {
      navigate(SETTINGS_URL, {
        state: { action: SETTING_FILTER_TYPE.CONNECTED_ACCOUNTS }
      })
    } else {
      const { connectAccountNumber } = exchangeProviderProfile
      changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {})
      validateAndConnectAccountNumber(connectAccountNumber)
    }
  }

  const isConnected = isProviderConnected(exchangeMemberships, provider)

  const handleExchangePoints = () => {
    resetRewardsState()
    const { pointsAmount } = exchangeProviderProfile
    changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {})
    const pointsErrors = checkValidations({
      pointsAmount: {
        value: pointsAmount,
        rules: [requiredRule, positiveNumberRule]
      }
    })
    if (containsNoValues(pointsErrors)) {
      const maxUsdExchangeLimit = provider.maxUsdExchangeLimit
      const tokenSymbol = tokenProfile.symbol
      const { minPoints, maxPoints } = calculateMinMaxPoints(
        { maxUsdExchangeLimit, minUsdExchangeLimit: MIN_USD_FOR_EXCHANGE },
        provider.exchangeOfframp
      )
      const validPointsErrorMessage = minPointsRule(
        pointsAmount,
        t,
        minPoints,
        tokenSymbol
      )
      if (currentUserPoints <= 0) {
        changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {
          pointsAmount: t(ERROR_TEXT.NOT_SUFFICIENT_AMOUNT)
        })
      } else if (validPointsErrorMessage) {
        changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {
          pointsAmount: validPointsErrorMessage
        })
      } else if (pointsAmount > currentUserPoints || pointsAmount > maxPoints) {
        changeExchangeProvidersState(EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR, {
          pointsAmount: t(ERROR_TEXT.EXCEED_MAX_LIMIT)
        })
      } else {
        setPopupState(EXCHANGE_PROVIDER_PROCESS_STEP.EXCHANGE_CONFIRM)
      }
    } else {
      changeExchangeProvidersState(
        EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
        pointsErrors
      )
    }
  }

  const handleNoPopup = () => {
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handleQrCodeClick = () => {
    setPopupState(QR_CODE_PROCESS_STEP.SCAN_CODE)
  }

  const handleCancelQrCode = () => {
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const handleQrCodeError = () => {
    setPopupState(QR_CODE_PROCESS_STEP.CAMERA_BLOCKED)
  }

  const handleQrCodeScan = (text) => {
    if (
      text &&
      !(exchangeProviderProfile.isProcessing || rewardsProfile.loading)
    ) {
      const accountNumber = extractCodeOrInputText(text)
      if (accountNumber) {
        validateAndConnectAccountNumber(accountNumber, true)
      }
    }
  }

  const validateAndConnectAccountNumber = (accountNumber, isQrScan) => {
    const { brandId } = provider
    const errors = checkValidations({
      connectAccountNumber: {
        value: accountNumber,
        rules: validationRulesForAccountNumberSanity(brandId)
      }
    })
    if (containsNoValues(errors)) {
      connectExchangeProvider(brandId, accountNumber)
    } else {
      if (isQrScan) {
        wrongAccountNumberToast()
      } else {
        changeExchangeProvidersState(
          EXCHANGE_PROVIDER_PROFILE_DISPATCH.ERROR,
          errors
        )
      }
    }
  }

  const handleClickSignup = async () => {
    if (signUpUrl) {
      openExternalUrl(signUpUrl)
    }
  }

  const handleAlertClose = () => {
    changeRewardsState(REWARDS_PROFILE_DISPATCH.REDEEM_STATE, '')
    changeExchangeProvidersInputAndErrorState(
      EXCHANGE_PROVIDER_PROFILE_DISPATCH.POINTS_AMOUNT,
      ''
    )
  }

  const handlePopupCancel = () => {
    handleNoPopup()
  }

  const handlePopupSubmit = () => {
    switch (popupState) {
      case EXCHANGE_PROVIDER_PROCESS_STEP.EXCHANGE_CONFIRM:
        const { pointsAmount } = exchangeProviderProfile
        receiveExternalRewardFromBrandPoints(
          pointsAmount,
          provider,
          extractMembershipNumber(exchangeMemberships, provider)
        )
        break

      default:
        break
    }
  }

  const renderPopup = () => {
    switch (popupState) {
      case EXCHANGE_PROVIDER_PROCESS_STEP.EXCHANGE_CONFIRM:
        return (
          <ReceiveExternalRewardsInit
            onSubmit={handlePopupSubmit}
            onCancel={handlePopupCancel}
            provider={provider}
            reward={{ value: exchangeProviderProfile.pointsAmount }}
            symbol={tokenProfile.symbol}
            cryptoExchanges={cryptoExchanges}
            rewardsProfile={rewardsProfile}
            t={t}
          />
        )

      case QR_CODE_PROCESS_STEP.SCAN_CODE:
        return (
          <ConnectAccountByQrCode
            onCancel={handleCancelQrCode}
            onError={handleQrCodeError}
            onScan={handleQrCodeScan}
            t={t}
          />
        )

      case QR_CODE_PROCESS_STEP.CAMERA_BLOCKED:
        return <CameraBlocked onCancel={handleCancelQrCode} t={t} />

      default:
        break
    }
  }

  const isKycNotSuccess = isKycIncomplete({
    status: kycProfile.status
  })

  const handleSetProvider = (provider) => {
    if (
      isKycNotSuccess ||
      !checkExchangeGating(provider, userTier.tier.metadata)
    ) {
      changeKYCState(KYC_PROFILE_DISPATCH.POPUP_STATE, {
        id: DEFAULT_CONTAINER_STATE.FEATURE_LOCKED,
        data: provider.isCrypto
          ? {
              targetTierId: EMERALD_LEVEL_ID
            }
          : {
              targetTierId: ONYX_LEVEL_ID
            }
      })
      setProvider(null)
    } else {
      setProvider(provider)
    }
  }

  return (
    <>
      <ExchangePoints
        selectedProvider={provider}
        onConnectProvider={handleConnectProvider}
        setProvider={handleSetProvider}
        onExchange={handleExchangePoints}
        onQrCodeClick={handleQrCodeClick}
        dispatchActionExchange={changeExchangeProvidersInputAndErrorState}
        exchangeProviderProfile={exchangeProviderProfile}
        onClickSignup={handleClickSignup}
        t={t}
        symbol={tokenProfile.symbol}
        cryptoExchanges={cryptoExchanges}
        exchangeWhitelist={exchangeWhitelist}
        isDisabledPointsBurn={isDisabledPointsBurn}
        signUpUrl={signUpUrl}
        isConnected={isConnected}
        onAlertClose={handleAlertClose}
        redeemState={rewardsProfile.redeemState}
        loading={rewardsProfile.loading}
        isKycNotSuccess={isKycNotSuccess}
        userTierMetadata={userTier.tier.metadata}
      />
      {renderPopup()}
    </>
  )
}

const mapStateToProps = ({
  mainReducer,
  rewardsReducer,
  exchangeReducer,
  accountReducer
}) => {
  const { mainProfile, tokenProfile } = mainReducer
  const { rewardsProfile } = rewardsReducer
  const { exchangeProviderProfile, cryptoExchangeProfile } = exchangeReducer
  const { cryptoExchanges } = cryptoExchangeProfile
  const { kycProfile } = accountReducer
  return {
    mainProfile,
    tokenProfile,
    rewardsProfile,
    exchangeProviderProfile,
    cryptoExchanges,
    kycProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeExchangeProvidersState,
      changeExchangeProvidersInputAndErrorState,
      connectExchangeProvider,
      resetExchangeProvider,
      receiveExternalRewardFromBrandPoints,
      resetRewardsState,
      changeRewardsState,
      getCryptoExchangeRates,
      stopGetCryptoExchangeRates,
      changeKYCState
    },
    dispatch
  )

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExchangePointsContainer)
